import React, { useState } from "react";

const Panel = ({
  title,
  subtitle,
  image,
  reverse = false,
  linkText,
  detail,
}) => {
  const [open, setOpen] = useState(false);

  const defaultDetail =
    "Our Business Conduct Policy sets out our ethical requirements for our employees. Each of our employees must certify that they have read and understand the policy when they join FOZZIL, and again each year. Violations of the policy are taken seriously and may result in disciplinary action.";

  return (
    <div className="max-w-6xl mx-auto px-6 py-6 relative">
      {!open ? (
        <div
          className={`relative group flex flex-col md:flex-row items-stretch gap-0 rounded-[32px] overflow-hidden border border-white/5 backdrop-blur-3xl shadow-2xl bg-black/40 hover:border-blue-500/20 transition-all duration-700`}
        >
          {/* Blue Shade Effect */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 blur-[80px] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
          
          <div
            className={`w-full md:w-1/2 flex flex-col justify-between p-12 md:p-14 text-white relative z-10 ${
              reverse ? "order-2 md:order-1" : "order-1"
            }`}
            style={{ minHeight: 420 }}
          >
            <div>
              <h2 className="font-primary text-4xl md:text-5xl mb-4 leading-tight tracking-tight group-hover:text-blue-400 transition-colors">
                {title}
              </h2>
              <p className="text-lg md:text-xl text-gray-400 mb-6 font-medium leading-relaxed">
                {subtitle}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setOpen(true)}
                className="flex items-center gap-3 text-sm font-semibold text-white/90 hover:text-white transition-colors cursor-pointer group/btn"
                aria-expanded={open}
              >
                <span className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center text-xl transition-all duration-500 group-hover/btn:scale-110 group-hover/btn:bg-blue-500 group-hover/btn:text-white">
                  +
                </span>
                <span className="ml-3 text-base tracking-tight font-bold tracking-widest">{linkText || "More"}</span>
              </button>
            </div>
          </div>

          <div
            className={`w-full md:w-1/2 relative z-10 ${
              reverse ? "order-1 md:order-2" : "order-2"
            }`}
          >
            <div className="w-full h-full relative overflow-hidden">
              <img
                src={image}
                alt=""
                className="w-full h-[420px] object-cover block opacity-80 grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`relative flex flex-col md:flex-row items-stretch gap-0 rounded-[32px] overflow-hidden border border-white/5 backdrop-blur-3xl shadow-2xl bg-black/60 transition-all duration-700`}
        >
          {/* Blue Shade Effect for Open State */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/15 blur-[90px] opacity-100 pointer-events-none" />
          
          <div
            className={`w-full md:w-1/2 flex flex-col justify-between p-12 md:p-14 text-white relative z-10 ${
              reverse ? "order-2 md:order-1" : "order-1"
            }`}
            style={{ minHeight: 420 }}
          >
            <div>
              <h2 className="font-primary text-4xl md:text-5xl mb-4 leading-tight tracking-tight text-blue-400">
                {title}
              </h2>
              <p className="text-lg md:text-xl text-gray-400 mb-6 font-medium leading-relaxed">
                {subtitle}
              </p>
            </div>

            <div className="flex items-center">
              <button
                onClick={() => setOpen(false)}
                className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center border border-white/10 hover:bg-blue-500 hover:border-blue-400 transition-all duration-500 cursor-pointer group/close"
                aria-label={`Close ${title}`}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="group-hover/close:scale-110 transition-transform"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div
            className={`w-full md:w-1/2 relative z-10 ${
              reverse ? "order-1 md:order-2" : "order-2"
            }`}
          >
            <div className="w-full h-full">
              <div className="w-full h-full min-h-[420px] bg-white/5 text-white/90 p-10 md:p-14 flex items-center justify-center border-l border-white/5 backdrop-blur-2xl shadow-2xl">
                <div className="max-w-md leading-relaxed text-lg md:text-xl font-medium tracking-tight">
                  {detail ? detail : <p>{defaultDetail}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const EthicsAndCompliance = () => {
  const pdfUrl =
    "https://img1.wsimg.com/blobby/go/b0b3e65e-c760-4844-b125-253596bb7178/Business%20Conduct%20of%20FOZZIL-ac4888a.pdf";

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-12 text-center">
        <h1 className="font-primary text-4xl md:text-[3.8rem] mb-6 tracking-tighter uppercase whitespace-normal md:whitespace-nowrap">
          Ethics and Compliance
        </h1>
        <p className="text-[12px] font-bold text-gray-500 uppercase tracking-[0.3em] mb-8">
          Revised Oct 2025
        </p>
        <p className="text-xl md:text-2xl text-gray-400 font-medium leading-normal max-w-3xl mx-auto">
          FOZZIL operates with integrity, ethics, and full legal compliance. 
          Our commitment to ethical standards ensures responsible innovation across the globe.
        </p>
      </div>

      <main className="space-y-16 py-16">
        <Panel
          title="Business Conduct Policy"
          subtitle="Honesty, Respect, Confidentiality, and Compliance"
          image="/fozzilbusinessconduct.png"
          linkText="Our Policy"
          detail={
            <p>
              Our{" "}
              <a
                href={pdfUrl}
                className="underline text-blue-400 hover:text-blue-300 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                Business Conduct Policy (PDF)
              </a>{" "}
              defines the ethical foundation for every FOZZIL employee. From day one, we commit to 
              transparency, accountability, and the highest standards of integrity in everything we build.
            </p>
          }
        />

        <Panel
          title="Compliance Framework"
          subtitle="Integrated, accountable, and designed for global readiness."
          image="/fozzilcomplaince.png"
          reverse
          linkText="Details"
          detail={
            <p>
              Compliance is woven into FOZZIL’s DNA. Our Compliance & Ethics Team oversees 
              AI Governance, Data Privacy (DPDP Act), and Anti-Corruption standards. 
              Through regular audits and mandatory training, we ensure integrity at every level of our organization.
            </p>
          }
        />

        <Panel
          title="Employee Principles"
          subtitle="Empowering our team to act with purpose and principle."
          image="/employee.png"
          linkText="Learn More"
          detail={
            <p>
              Every team member completes annual Ethics Training, reinforcing our culture of 
              transparency. With direct access to the FOZZIL Ethics Helpline and Compliance Portal, 
              we provide the tools for every individual to lead with excellence.
            </p>
          }
        />
      </main>
    </div>
  );
};

export const EthicsCompliance = () => null;
